import React, { useState, useRef, useEffect } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useTranslation } from 'react-i18next';
import { setI18nLanguage } from '../../i18n/config';
import i18n from '../../i18n/config';

const categoryOptions = ["Donation", "Grant", "Others"];

function ExpensesPage({ onAddExpense, onHome, onBack, onEditProfile }) {
  const { user, logout } = useAuth0();
  const { t } = useTranslation('expenses');
  const { t: tc } = useTranslation('common');

  const [minAmount, setMinAmount] = useState("");
  const [maxAmount, setMaxAmount] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [search, setSearch] = useState("");

  // ---- Language menu state ----
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  const langMenuRef = useRef(null);

  // ---- Profile dropdown state ----
  const [profileOpen, setProfileOpen] = useState(false);
  const profileRef = useRef(null);

  useEffect(() => {
    if (!profileOpen) return;

    const handleClickOutside = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setProfileOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () =>
      document.removeEventListener("mousedown", handleClickOutside);
  }, [profileOpen]);

  // Close the language menu on outside click.
  useEffect(() => {
    if (!langMenuOpen) return;

    const handleClickOutside = (e) => {
      if (
        langMenuRef.current &&
        !langMenuRef.current.contains(e.target)
      ) {
        setLangMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () =>
      document.removeEventListener("mousedown", handleClickOutside);
  }, [langMenuOpen]);

  // Change language
  const handleLanguageChange = (lang) => {
    setI18nLanguage(lang);
    localStorage.setItem("zbookkeeping_language", lang);
    setLangMenuOpen(false);
  };

  const handleLogout = () => {
    setProfileOpen(false);
    logout({ logoutParams: { returnTo: window.location.origin } });
  };

  const handleEditProfile = () => {
    setProfileOpen(false);
    if (onEditProfile) onEditProfile();
  };

  const handleReset = () => {
    setMinAmount("");
    setMaxAmount("");
    setSelectedCategory("");
  };

  return (
    <div className="min-h-screen bg-gray-50">

      {/* Top bar */}
      <div className="bg-white border-b border-gray-200 px-4 py-4 flex items-center gap-3">

        <button
          onClick={onHome}
          aria-label="Home"
          title="Home"
          className="text-blue-600 hover:text-blue-700 flex-shrink-0"
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            className="fill-current"
          >
            <path d="M12 3.2 3 10v10h6v-6h6v6h6V10l-9-6.8Z" />
          </svg>
        </button>

        <button
          onClick={onBack}
          aria-label="Back"
          className="text-blue-600 hover:text-blue-700"
        >
          <svg
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
          >
            <path
              d="M19 12H5M5 12l7-7M5 12l7 7"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </button>

        <span className="text-sm font-medium text-gray-900 flex-1">
          {t('title')}
        </span>

        <div className="flex-1" />

        {/* Language toggle */}
        <div ref={langMenuRef} className="relative">
          <button
            onClick={() => setLangMenuOpen((open) => !open)}
            aria-label="Language menu"
            aria-expanded={langMenuOpen}
            className="text-xs font-medium text-gray-600 border border-gray-200 rounded-lg px-2.5 py-1.5 hover:bg-gray-50 transition"
          >
            {i18n.language === "ml" ? "മലയാളം" : "EN"}
          </button>

          {langMenuOpen && (
            <div className="absolute right-0 top-10 w-32 bg-white rounded-xl shadow-lg border border-gray-100 p-2 z-50">

              <button
                onClick={() => handleLanguageChange("en")}
                className={`w-full text-left rounded-lg px-3 py-2 text-sm transition ${
                  i18n.language === "en"
                    ? "bg-blue-50 text-blue-700 font-medium"
                    : "text-gray-700 hover:bg-gray-50"
                }`}
              >
                English
              </button>

              <button
                onClick={() => handleLanguageChange("ml")}
                className={`w-full text-left rounded-lg px-3 py-2 text-sm transition ${
                  i18n.language === "ml"
                    ? "bg-blue-50 text-blue-700 font-medium"
                    : "text-gray-700 hover:bg-gray-50"
                }`}
              >
                മലയാളം
              </button>

            </div>
          )}
        </div>

        {/* Profile avatar + dropdown */}
        <div ref={profileRef} className="relative">

          <button
            onClick={() => setProfileOpen((open) => !open)}
            aria-label="Account menu"
            aria-expanded={profileOpen}
            className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white text-xs font-medium"
          >
            {user?.name?.charAt(0).toUpperCase() || "U"}
          </button>

          {profileOpen && (
            <div className="absolute right-0 top-11 w-64 bg-white rounded-xl shadow-lg border border-gray-100 p-4 z-50">

              <p className="text-sm text-gray-900 truncate">
                {user?.email}
              </p>

              <p className="text-sm text-gray-400 truncate mb-4">
                {user?.email}
              </p>

              <button
                onClick={handleEditProfile}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-lg py-2.5 text-sm font-medium transition mb-2"
              >
                {tc('editProfile')}
              </button>

              <button
                onClick={handleLogout}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-lg py-2.5 text-sm font-medium transition"
              >
                {tc('logout')}
              </button>

            </div>
          )}
        </div>
      </div>

      {/* Page content */}
      <div className="max-w-sm mx-auto px-4 py-8">

        <h1 className="text-center text-base font-medium text-blue-600 mb-6 uppercase tracking-wider">
          {t('title')}
        </h1>

        {/* Add Expenses */}
        <button
          type="button"
          onClick={onAddExpense}
          className="mb-6 w-full bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-4 py-3 text-sm font-medium transition"
        >
          {t('addButton')}
        </button>

        {/* Search */}
        <div className="mb-4">

          <p className="text-xs text-gray-500 uppercase tracking-widest mb-1.5">
            {t('searchLabel')}
          </p>

          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full bg-gray-100 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400 border-0"
          />

        </div>

        {/* Filters */}
        <p className="text-xs text-gray-500 uppercase tracking-widest mb-2">
          {t('filters')}
        </p>

        <div className="bg-white border border-gray-200 rounded-xl px-3.5 py-3.5 mb-8">

          {/* Date */}
          <div className="mb-3">

            <p className="text-xs text-gray-500 uppercase tracking-widest mb-1.5">
              {t('dateLabel')}
            </p>

            <div className="flex gap-2">

              <input
                type="date"
                className="flex-1 bg-gray-100 rounded-lg px-2 py-2 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400 border-0"
              />

              <input
                type="date"
                className="flex-1 bg-gray-100 rounded-lg px-2 py-2 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400 border-0"
              />

            </div>
          </div>

          {/* Category */}
          <div className="mb-3">

            <p className="text-xs text-gray-500 uppercase tracking-widest mb-1.5">
              {t('categoryLabel')}
            </p>

            <div className="grid grid-cols-3 gap-2">

              {categoryOptions.map((cat) => (
                <button
                  key={cat}
                  type="button"
                  onClick={() => setSelectedCategory(cat)}
                  className={`rounded-lg px-2 py-2 text-xs font-medium transition ${
                    selectedCategory === cat
                      ? "bg-blue-100 text-blue-700"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  {cat}
                </button>
              ))}

            </div>
          </div>

          {/* Amount */}
          <div className="mb-3">

            <p className="text-xs text-gray-500 uppercase tracking-widest mb-1.5">
              {t('amountRange')}
            </p>

            <div className="grid grid-cols-2 gap-2">

              <input
                type="number"
                placeholder="Min"
                value={minAmount}
                onChange={(e) => setMinAmount(e.target.value)}
                className="bg-gray-100 rounded-lg px-2 py-2 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400 border-0"
              />

              <input
                type="number"
                placeholder="Max"
                value={maxAmount}
                onChange={(e) => setMaxAmount(e.target.value)}
                className="bg-gray-100 rounded-lg px-2 py-2 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400 border-0"
              />

            </div>
          </div>

          {/* Buttons */}
          <div className="flex gap-2">

            <button
              type="button"
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white rounded-lg py-2 text-xs font-medium transition"
            >
              {tc('apply')}
            </button>

            <button
              type="button"
              onClick={handleReset}
              className="flex-1 border border-gray-300 text-gray-600 hover:bg-gray-50 rounded-lg py-2 text-xs font-medium transition"
            >
              {tc('reset')}
            </button>

          </div>
        </div>

        {/* Expenses list */}
        <h2 className="text-center text-xs text-gray-500 uppercase tracking-widest">
          {t('listTitle')}
        </h2>

      </div>
    </div>
  );
}

export default ExpensesPage;